import { dashFetch } from "@/api/client";

/** Wizard step indices (must match AddSiteWizard STEPS). */
export const ADD_SITE_STEP = {
  domain: 0,
  verify: 1,
  website: 2,
  content: 3,
  dns: 4,
  done: 5
} as const;

export type WizardResumeState = {
  ok?: boolean;
  step?: number;
  resumeStep?: number;
  txtVerified?: boolean;
  aRecordVerified?: boolean;
  tenantStatus?: string;
  dashboardPreview?: boolean;
  verification?: { status?: string };
  onboardingWizard?: { lastStep?: number; dashboardPreview?: boolean };
};

/** Pick wizard step from verification / tenant state (pending resume). */
export function wizardStepFromResumeState(resp: WizardResumeState | null | undefined): number {
  if (!resp?.ok) return ADD_SITE_STEP.verify;

  const step = Number(resp.step ?? resp.resumeStep);
  if (Number.isFinite(step) && step >= 0 && step <= ADD_SITE_STEP.done) {
    return step;
  }

  const vStatus = String(resp.verification?.status || "").toLowerCase();
  const txt =
    Boolean(resp.txtVerified) || vStatus === "verified" || vStatus === "active";
  const aRecord = Boolean(resp.aRecordVerified);

  if (!txt) return ADD_SITE_STEP.verify;
  if (txt && !aRecord) return ADD_SITE_STEP.website;
  return ADD_SITE_STEP.dns;
}

export async function fetchAddSiteWizardResumeStep(domain: string): Promise<number> {
  const d = String(domain || "").trim();
  if (!d) return ADD_SITE_STEP.domain;

  try {
    const resp = await dashFetch<WizardResumeState>(
      `/dashboard/api/tenants/wizard-state?domain=${encodeURIComponent(d)}`,
      { cache: "no-store", site: d }
    );
    return wizardStepFromResumeState(resp);
  } catch {
    return ADD_SITE_STEP.verify;
  }
}

export async function saveAddSiteWizardProgress(
  domain: string,
  step: number,
  dashboardPreview?: boolean
): Promise<void> {
  const d = String(domain || "").trim();
  if (!d) return;
  try {
    await dashFetch("/dashboard/api/tenants/wizard-progress", {
      method: "POST",
      headers: { "content-type": "application/json" },
      site: d,
      body: JSON.stringify({ domain: d, step, dashboardPreview })
    });
  } catch {
    /* ignore */
  }
}
